import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Dropdown,
  Header,
  Icon,
  Image,
  Segment,
} from 'semantic-ui-react';
import TruncateMarkup from 'react-truncate-markup';
import { Viewer, Worker } from '@react-pdf-viewer/core';

import dayjs from 'src/libs/dayjs';
import { Alert, CommentIcon, Heart } from 'src/components/icons';
import defaultProfile from 'src/assets/default_profile.jpeg';
import { useAppSelector } from 'src/store';
import { Modal } from 'src/components/custom';
import { downloadLink, getFileEtx } from 'src/utils/helper';
import { HOUR_MIN } from 'src/constants/app';

type Props = {
  id?: string;
  userId?: string;
  profileUrl?: string;
  name?: string;
  date?: string;
  fileUrl?: string;
  fileName?: string;
  onComment?: (id?: string) => void;
  onLike?: () => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  text?: React.ReactNode;
  showDropDown?: boolean;
  numberOfComments?: number;
  numberOfLike?: number;
  showReaction?: boolean;
  files?: File[];
  onReply?: () => void | Promise<any>;
  error?: string | boolean;
  onError?: () => void | Promise<any>;
  relax?: boolean;
};

type File = { url: string; name: string; type?: string } & Record<string, any>;

const Comment: React.FC<Props> & {
  Group: React.FC;
  Files: React.FC<{ files: File[]; canDownload?: boolean }>;
  Button: React.FC<{
    children?: string;
    onClick?: (next: number | null) => void | Promise<any>;
    loading?: boolean;
    user_assignment_file_id?: string;
  }>;
} = ({
  id,
  userId,
  profileUrl,
  name,
  date,
  children,
  text,
  onEdit,
  onDelete,
  numberOfLike = 0,
  numberOfComments = 1,
  showReaction,
  files = [],
  onComment,
  onReply,
  error,
  onError,
  relax,
}) => {
  const user_id = useAppSelector(
    useCallback((state) => state.ui.app.me?.id, []),
  );

  const hasChild = useMemo(() => {
    if (children) {
      if (React.Children.count(children)) {
        const firstChild = React.Children.toArray(children)[0];

        return !!firstChild;
      }

      return false;
    } else {
      return false;
    }
  }, [children]);

  return (
    <div className="submission" key={id} id={id}>
      <div className="head">
        <div className="flex-shrink-0">
          <Image
            width={40}
            height={40}
            circular
            src={profileUrl || defaultProfile}
            className="object-fit-cover"
          />
        </div>
        <div className="author">
          <div className={`name ${user_id === userId ? 'text-primary' : ''}`}>
            {name}
          </div>
          <div className="date">
            {dayjs(date).fromNow()}, {dayjs(date).format(HOUR_MIN)}
          </div>
        </div>
        {(onDelete || onEdit) && (
          <Dropdown
            icon={null}
            trigger={<Icon name="ellipsis horizontal" color="grey" />}
            direction="left"
          >
            <Dropdown.Menu>
              {onEdit && (
                <Dropdown.Item onClick={() => onEdit(id!)}>Edit</Dropdown.Item>
              )}
              {onDelete && (
                <Dropdown.Item onClick={() => onDelete(id!)}>
                  Delete
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>

      <div className="content">
        <Comment.Files files={files} canDownload />
        {text}
      </div>

      {(onComment || onReply) && (
        <div className="actions">
          {onReply && (
            <a onClick={() => onReply?.()} className="reply">
              REPLY
            </a>
          )}
          {!!numberOfLike && (
            <React.Fragment>
              <Heart /> {numberOfLike}
            </React.Fragment>
          )}
          {!!numberOfComments && (
            <React.Fragment>
              <CommentIcon
                onClick={() => onComment?.(id)}
                className={onComment ? 'cursor-pointer' : ''}
              />
              <span className="ml-3">{numberOfComments}</span>
            </React.Fragment>
          )}
        </div>
      )}
      {error && (
        <div className="fs-14 d-flex align-center text-danger mt-4">
          <Alert />{' '}
          <span className="ml-4">
            {error && typeof error === 'string' ? (
              error
            ) : (
              <span>
                Please try{' '}
                <span
                  onClick={() => {
                    onError?.();
                  }}
                  className="text-underline text-danger cursor-pointer"
                >
                  sending your comment again.
                </span>
              </span>
            )}
          </span>
        </div>
      )}

      {hasChild && (
        <div className={`child ${relax ? 'relax' : ''}`}>{children}</div>
      )}
    </div>
  );
};

Comment.Group = ({ children }) => {
  return <div className="submissions">{children}</div>;
};

Comment.Files = ({ files, canDownload }) => {
  const [file, setFile] = useState<File | undefined>();

  const renderModalContent = (url: string) => {
    switch (getFileEtx(url)) {
      case 'file pdf outline':
        return <Viewer fileUrl={url} />;
      case 'file image outline':
        return <Image centered src={url} />;
      default:
        return (
          <Segment placeholder>
            <Header icon>
              <Icon name="file alternate" />
              No preview.
            </Header>
          </Segment>
        );
    }
  };

  return (
    <div className="attached_files">
      {!!files?.length &&
        files.map((file) => {
          return (
            getFileEtx(file.url) && (
              <div
                key={file.name}
                className="attached_file cursor-pointer"
                onClick={() => setFile(file)}
              >
                <div className="file_content">
                  {getFileEtx(file.url) === 'file image outline' ? (
                    <img src={file.url} alt="img" />
                  ) : (
                    <Icon name={getFileEtx(file.url)} size="big" />
                  )}
                </div>
                <div className="file_name">
                  <TruncateMarkup lines={1}>
                    <span>{file.name}</span>
                  </TruncateMarkup>
                </div>
              </div>
            )
          );
        })}
      <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.13.216/build/pdf.worker.min.js">
        <Modal
          visible={!!file}
          size="fullscreen"
          onClose={() => setFile(undefined)}
          content={
            file?.url ? (
              <div className="preview">
                {canDownload && (
                  <Button
                    className="mb-24"
                    primary
                    onClick={() => downloadLink(file?.url)}
                  >
                    Download
                  </Button>
                )}
                {renderModalContent(file.url)}
              </div>
            ) : (
              <div />
            )
          }
        />
      </Worker>
    </div>
  );
};

Comment.Button = ({ children, onClick, user_assignment_file_id, loading }) => {
  const [isLoading, setIsLoading] = useState<boolean | undefined>(loading);
  const loadmore = useAppSelector(
    (state) => state.model.homework_comment.loadMore,
  );

  const itemInfo = loadmore.find(
    (item) => item.user_assignment_file_id === user_assignment_file_id,
  );

  const handleClick = async () => {
    setIsLoading(true);

    if (itemInfo && itemInfo.pagination) {
      const {
        total_pages = 0,
        current_page = 0,
        next_page = 0,
      } = itemInfo.pagination;
      const hasLoadmore = current_page < total_pages || next_page !== null;

      if (hasLoadmore) {
        await onClick?.(next_page);
      }
    } else {
      await onClick?.(1);
    }
    setIsLoading(false);
  };

  return (
    <div
      className="text-gray text-center cursor-pointer mt-6"
      onClick={handleClick}
    >
      {isLoading && <Icon loading name="spinner" />}
      {children}
    </div>
  );
};

Comment.Group.displayName = 'CommentGroup';
Comment.Files.displayName = 'AttachFiles';
Comment.Button.displayName = 'CommentButton';

export default Comment;
